import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import AntDesign from 'react-native-vector-icons/AntDesign';
import { FolderIcon } from '../../../resources/svgComponents/FolderIcon';
import { mbTextStyles } from '@/utils/general/style-utils';
import { textStyles } from '../../../constants/textStyles';
import { AddFolderIcon } from '../../../resources/svgComponents/AddFolderIcon';
import { MB_utilHooks } from '@/utils/general/util-hooks';
import { MB_Modal } from '@/components/general/modal';
import { MB_TextInput } from '@/components/general/text-input';
import { MB_Button } from '@/components/general/button';
import { mbHidePopUp, mbShowPopUp } from '@/components/general/pop-up';
import { AddFolderModal } from './AddFolderModal';
import { useDeleteFolder, useEditFolder, useGetFolders } from '../../../hooks/adminHooks';
import { COLORS } from '../../../constants/colors';
import { SERVER_ERROR_CODES, STRING_CONSTANTS } from '../../../constants/constants';
import { FOLDER_TYPE, Folder } from '../../../typesAndInterfaces/typesAndInterfaces';

const FoldersView = ({ type, onFolderPress, disableAddFolder }: { type: FOLDER_TYPE; onFolderPress?: (folder: Folder) => void; disableAddFolder?: boolean }) => {
  const [isAddFolderVisible, showAddFolderModal, hideAddFolderModal] = MB_utilHooks.useBool();

  const { data: folders } = useGetFolders({ type });
  const { mutate: editFolder } = useEditFolder();
  const { mutate: deleteFolder } = useDeleteFolder();

  const [renameTarget, setRenameTarget] = useState<Folder | undefined>(undefined);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | undefined>(undefined);

  // Folder management is an admin affordance; the item-picker modals pass
  // disableAddFolder and should not expose rename/delete either.
  const allowManage = !disableAddFolder;

  const onRenameConfirm = useCallback(() => {
    const trimmed = renameValue.trim();
    if (!renameTarget || trimmed.length === 0) {
      setRenameError('Name is required.');
      return;
    }
    editFolder(
      { folderId: renameTarget._id, name: trimmed },
      {
        onSuccess: () => setRenameTarget(undefined),
        onError: error =>
          setRenameError(error?.errorCode === SERVER_ERROR_CODES.FOLDER_ALREADY_EXISTS ? 'A folder with this name already exists.' : STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN),
      }
    );
  }, [editFolder, renameTarget, renameValue]);

  const onDeletePress = useCallback(
    (folder: Folder) => {
      mbShowPopUp({
        title: 'Delete folder',
        message: `Are you sure you want to delete “${folder.name}”? Folders can only be deleted when they are empty.`,
        buttonText: 'Delete folder',
        buttonAction: () => {
          return new Promise((resolve, reject) => {
            deleteFolder(
              { folderId: folder._id },
              {
                onSuccess: () => {
                  resolve();
                  mbHidePopUp();
                },
                onError: error => {
                  reject(error?.errorCode === SERVER_ERROR_CODES.FOLDER_NOT_EMPTY ? 'This folder is not empty. Move or delete its contents first.' : STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
                },
              }
            );
          });
        },
        buttonStyle: { container: { backgroundColor: COLORS.errorColor } },
        containerStyle: { width: 454 },
        secondaryButtonText: 'Cancel',
        secondaryButtonStyle: {
          text: { color: COLORS.buttonPurple },
          container: { backgroundColor: COLORS.white },
        },
      });
    },
    [deleteFolder]
  );

  return (
    <View style={styles.container}>
      <AddFolderModal key={`${isAddFolderVisible}`} isVisible={isAddFolderVisible} onDismiss={hideAddFolderModal} type={type} />
      <Text style={styles.title}>Folders</Text>
      <View style={styles.folders}>
        {folders?.map(folder => (
          <View key={folder._id} style={styles.folderWrapper}>
            <TouchableOpacity style={styles.folder} onPress={() => onFolderPress?.(folder)} testID={`folder-card-${folder.name}`}>
              <FolderIcon />
              <Text style={styles.folderNameText} numberOfLines={2}>
                {folder.name}
              </Text>
            </TouchableOpacity>
            {allowManage && (
              <>
                <MB_Button
                  style={styles.folderEditButton}
                  leftElement={<Feather name="edit-2" size={12} color={COLORS.white} />}
                  onPress={() => {
                    setRenameTarget(folder);
                    setRenameValue(folder.name);
                    setRenameError(undefined);
                  }}
                />
                <MB_Button
                  style={styles.folderDeleteButton}
                  leftElement={<AntDesign name="close" size={14} color={COLORS.lighterGray} />}
                  onPress={() => onDeletePress(folder)}
                />
              </>
            )}
          </View>
        ))}
        {!disableAddFolder && (
          <View style={styles.folderWrapper}>
            <TouchableOpacity style={styles.folder} onPress={showAddFolderModal}>
              <AddFolderIcon />
              <Text style={styles.folderNameText}>Create new folder</Text>
            </TouchableOpacity>
          </View>
        )}
        {new Array(20).fill(1).map((_, index) => (
          <View key={`fake-folder-${index}`} style={styles.fakeFolder} />
        ))}
      </View>

      <MB_Modal isVisible={renameTarget !== undefined} onDismiss={() => setRenameTarget(undefined)} title="Rename folder" childrenWrapperStyle={styles.renameModal}>
        <View style={styles.renameModalBody}>
          <Text style={styles.renameLabel}>Name*</Text>
          <MB_TextInput
            value={renameValue}
            onChangeText={text => {
              setRenameValue(text);
              setRenameError(undefined);
            }}
            isError={renameError !== undefined}
            maxLength={30}
          />
          {renameError !== undefined && <Text style={styles.renameErrorText}>{renameError}</Text>}
          <View style={styles.renameButtons}>
            <MB_Button title="Cancel" style={styles.renameCancelButton} textStyle={{ color: COLORS.buttonPurple }} onPress={() => setRenameTarget(undefined)} />
            <MB_Button title="Save" style={styles.renameSaveButton} onPress={onRenameConfirm} />
          </View>
        </View>
      </MB_Modal>
    </View>
  );
};

export { FoldersView };

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginBottom: 48,
  },
  title: mbTextStyles([
    textStyles.smallText,
    {
      textAlign: 'left',
    },
  ]),
  folders: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  fakeFolder: {
    width: 156,
  },
  folderWrapper: {
    width: 156,
  },
  folder: {
    marginTop: 20,
    width: 120,
    alignItems: 'center',
  },
  folderNameText: mbTextStyles([
    textStyles.smallText,
    {
      marginTop: 12,
    },
  ]),
  folderEditButton: {
    width: 24,
    height: 24,
    borderRadius: 300,
    position: 'absolute',
    backgroundColor: COLORS.buttonPurple,
    top: 12,
    right: 46,
    paddingHorizontal: 0,
  },
  folderDeleteButton: {
    width: 24,
    height: 24,
    borderRadius: 300,
    position: 'absolute',
    backgroundColor: 'red',
    top: 12,
    right: 18,
    paddingHorizontal: 0,
  },
  renameModal: {
    width: 420,
    padding: 24,
  },
  renameModalBody: {
    width: '100%',
  },
  renameLabel: mbTextStyles([textStyles.normalText, { textAlign: 'left', marginBottom: 8 }]),
  renameErrorText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.errorColor,
      textAlign: 'left',
      marginTop: 6,
    },
  ]),
  renameButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  renameCancelButton: {
    backgroundColor: COLORS.white,
    marginRight: 12,
    paddingHorizontal: 20,
  },
  renameSaveButton: {
    backgroundColor: COLORS.buttonPurple,
    paddingHorizontal: 24,
  },
});
